/**
 * Surveys API Service
 * Handles survey creation, management, and response collection
 */

import { apiClient, handleApiError } from './apiClient';

// ============================================================================
// TYPES
// ============================================================================

export interface SurveyQuestion {
  question_text: string;
  question_type: 'text' | 'number' | 'multiple_choice' | 'yes_no' | 'scale';
  options?: string[];
  required: boolean;
  order: number;
  validation_rules?: Record<string, any>;
}

export interface Survey {
  id: string;
  name: string;
  description?: string;
  organization_id: number;
  program_id?: number;
  status: 'draft' | 'active' | 'archived';
  response_channel: 'sms' | 'email' | 'web';
  collect_anonymous: boolean;
  question_count: number;
  response_count: number;
  completion_rate: number;
  created_by: number;
  created_at: string;
  last_updated_at?: string;
}

export interface CreateSurveyRequest {
  name: string;
  description?: string;
  organization_id: number;
  program_id?: number;
  questions: SurveyQuestion[];
  response_channel?: 'sms' | 'email' | 'web';
  collect_anonymous?: boolean;
}

export interface UpdateSurveyRequest {
  name?: string;
  description?: string;
  questions?: SurveyQuestion[];
  status?: 'draft' | 'active' | 'archived';
}

export interface SurveyQuestionResponse {
  id: string;
  survey_id: string;
  question_text: string;
  question_type: 'text' | 'number' | 'multiple_choice' | 'yes_no' | 'scale';
  options?: string[];
  required: boolean;
  order: number;
  validation_rules?: Record<string, any>;
}

export interface SubmitSurveyResponseRequest {
  survey_id: string;
  member_id?: string;
  phone_number?: string;
  answers: Record<string, any>;
}

export interface SurveyResponse {
  id: string;
  survey_id: string;
  member_id?: string;
  phone_number?: string;
  answers: Record<string, any>;
  is_complete: boolean;
  response_duration_seconds?: number;
  submitted_at: string;
}

export interface SurveyAnalytics {
  survey_id: string;
  survey_name: string;
  total_responses: number;
  completion_rate: number;
  avg_response_time_seconds?: number;
  question_analytics: any[];
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

export const surveysApi = {
  /**
   * List surveys
   */
  async listSurveys(params?: {
    organization_id?: number;
    program_id?: number;
    status_filter?: string;
    limit?: number;
    offset?: number;
  }): Promise<Survey[]> {
    try {
      const response = await apiClient.get('/api/surveys', { params });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Get survey by ID
   */
  async getSurvey(surveyId: string): Promise<Survey> {
    try {
      const response = await apiClient.get(`/api/surveys/${surveyId}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Get survey questions
   */
  async getSurveyQuestions(surveyId: string): Promise<SurveyQuestionResponse[]> {
    try {
      const response = await apiClient.get(`/api/surveys/${surveyId}/questions`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Create survey
   */
  async createSurvey(survey: CreateSurveyRequest): Promise<Survey> {
    try {
      const response = await apiClient.post('/api/surveys', survey);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Update survey
   */
  async updateSurvey(surveyId: string, updates: UpdateSurveyRequest): Promise<Survey> {
    try {
      const response = await apiClient.put(`/api/surveys/${surveyId}`, updates);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Delete survey
   */
  async deleteSurvey(surveyId: string): Promise<void> {
    try {
      await apiClient.delete(`/api/surveys/${surveyId}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Submit survey response
   */
  async submitSurveyResponse(response: SubmitSurveyResponseRequest): Promise<SurveyResponse> {
    try {
      const result = await apiClient.post('/api/surveys/responses', response);
      return result.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Get survey responses
   */
  async getSurveyResponses(
    surveyId: string,
    params?: {
      member_id?: string;
      limit?: number;
      offset?: number;
    }
  ): Promise<SurveyResponse[]> {
    try {
      const response = await apiClient.get(`/api/surveys/${surveyId}/responses`, { params });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Get survey analytics
   */
  async getSurveyAnalytics(surveyId: string): Promise<SurveyAnalytics> {
    try {
      const response = await apiClient.get(`/api/surveys/${surveyId}/analytics`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },
};
